import React, { useEffect, useRef, useState } from 'react';
import { CalendarPlus, Calendar as CalendarIcon, Download } from 'lucide-react';
import { toast } from 'sonner';

// Summit day is September 17, 2026 in America/New_York (EDT = UTC-4).
const SUMMIT_YEAR = 2026;
const SUMMIT_MONTH = 9;
const SUMMIT_DAY = 17;
const TZ_OFFSET_HOURS = -4; // EDT

// Parse "8:00 AM – 9:00 AM" (accepts – or -) into two Date objects in UTC.
const parseSessionTimes = (timeStr) => {
  if (!timeStr) return null;
  const parts = String(timeStr).split(/[–—-]/).map((s) => s.trim());
  if (parts.length !== 2) return null;
  const [start, end] = parts;
  const toUtc = (t) => {
    const m = t.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!m) return null;
    let h = parseInt(m[1], 10);
    const min = parseInt(m[2], 10);
    const mer = m[3].toUpperCase();
    if (mer === 'PM' && h !== 12) h += 12;
    if (mer === 'AM' && h === 12) h = 0;
    const utcH = h - TZ_OFFSET_HOURS; // EDT → UTC (add 4)
    return new Date(Date.UTC(SUMMIT_YEAR, SUMMIT_MONTH - 1, SUMMIT_DAY, utcH, min, 0));
  };
  const startDt = toUtc(start);
  const endDt = toUtc(end);
  if (!startDt || !endDt) return null;
  return { start: startDt, end: endDt };
};

const fmtGCal = (d) =>
  d.getUTCFullYear().toString() +
  String(d.getUTCMonth() + 1).padStart(2, '0') +
  String(d.getUTCDate()).padStart(2, '0') +
  'T' +
  String(d.getUTCHours()).padStart(2, '0') +
  String(d.getUTCMinutes()).padStart(2, '0') +
  '00Z';

const escapeIcs = (s) =>
  String(s || '')
    .replace(/\\/g, '\\\\')
    .replace(/\n/g, '\\n')
    .replace(/,/g, '\\,')
    .replace(/;/g, '\\;');

const buildIcs = ({ title, description, location, start, end, uid }) => {
  const now = fmtGCal(new Date());
  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//AAWTA//Workforce Summit//EN',
    'CALSCALE:GREGORIAN',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${now}`,
    `DTSTART:${fmtGCal(start)}`,
    `DTEND:${fmtGCal(end)}`,
    `SUMMARY:${escapeIcs(title)}`,
    `DESCRIPTION:${escapeIcs(description)}`,
    `LOCATION:${escapeIcs(location)}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');
};

const downloadIcs = (filename, content) => {
  const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export const AddToCalendar = ({
  time,
  title,
  description = '',
  location = 'Stone Ridge Event Center, 1750 Stone Ridge Drive, Suite A, Stone Mountain, GA 30083',
  url = '',
  testIdPrefix = 'atc',
  compact = false,
  buttonLabel = 'Add to calendar',
  buttonClassName = '',
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const parsed = parseSessionTimes(time);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  if (!parsed) return null;
  const { start, end } = parsed;
  const detailsText = [description, url && `Details: ${url}`].filter(Boolean).join('\n\n');
  const uid = `aawta-${start.getTime()}-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 40)}@aawta.org`;
  const icsFile = `aawta-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 40)}.ics`;

  const googleUrl =
    'https://calendar.google.com/calendar/render?' +
    new URLSearchParams({
      action: 'TEMPLATE',
      text: title,
      dates: `${fmtGCal(start)}/${fmtGCal(end)}`,
      details: detailsText,
      location,
    }).toString();

  const yahooUrl =
    'https://calendar.yahoo.com/?' +
    new URLSearchParams({
      v: '60',
      title,
      st: fmtGCal(start),
      et: fmtGCal(end),
      desc: detailsText,
      in_loc: location,
    }).toString();

  const outlookUrl =
    'https://outlook.live.com/calendar/0/deeplink/compose?' +
    new URLSearchParams({
      path: '/calendar/action/compose',
      rru: 'addevent',
      subject: title,
      startdt: start.toISOString(),
      enddt: end.toISOString(),
      body: detailsText,
      location,
    }).toString();

  const handleIcs = () => {
    const ics = buildIcs({ title, description: detailsText, location, start, end, uid });
    downloadIcs(icsFile, ics);
    toast.success('Calendar file downloaded — open it to add to Apple Calendar');
  };

  const openWin = (u) => window.open(u, '_blank', 'noopener,noreferrer');

  const targets = [
    { key: 'google', label: 'Google Calendar', onClick: () => openWin(googleUrl) },
    { key: 'apple', label: 'Apple Calendar (.ics)', onClick: handleIcs, icon: Download },
    { key: 'outlook', label: 'Outlook', onClick: () => openWin(outlookUrl) },
    { key: 'yahoo', label: 'Yahoo', onClick: () => openWin(yahooUrl) },
  ];

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid={`${testIdPrefix}-toggle`}
        className={
          buttonClassName ||
          `inline-flex items-center gap-1.5 text-[${compact ? '11.5' : '12.5'}px] font-semibold text-aawta-navy hover:text-aawta-magenta rounded-full px-3 py-1.5 border border-aawta-border bg-white hover:border-aawta-magenta/40 transition-colors`
        }
      >
        <CalendarPlus className={compact ? 'h-3 w-3' : 'h-3.5 w-3.5'} />
        {buttonLabel && <span>{buttonLabel}</span>}
      </button>

      {open && (
        <div
          role="menu"
          data-testid={`${testIdPrefix}-menu`}
          className="absolute right-0 z-40 mt-2 w-56 rounded-xl border border-aawta-border bg-white shadow-aawta-elevated overflow-hidden"
        >
          <div className="px-4 py-2 bg-aawta-surface border-b border-aawta-border">
            <div className="text-[10.5px] font-bold uppercase tracking-wider text-aawta-muted">Add to Calendar</div>
            <div className="text-[12px] text-aawta-navy font-medium mt-0.5">Sep 17, 2026 · {time}</div>
          </div>
          {targets.map((t) => {
            const Icon = t.icon || CalendarIcon;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => { t.onClick(); setOpen(false); }}
                data-testid={`${testIdPrefix}-${t.key}`}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-[13.5px] text-aawta-navy hover:bg-aawta-surface text-left"
              >
                <Icon className="h-4 w-4 text-aawta-magenta shrink-0" />
                <span className="font-medium">{t.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AddToCalendar;
